import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import { PlusIcon, XMarkIcon, ListBulletIcon, CheckIcon, ChatBubbleLeftRightIcon, ClockIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const priorities = ['high', 'medium', 'low'];
const areas = ['recruitment', 'onboarding', 'compliance', 'observation', 'one_on_one', 'food_program', 'general'];
const statuses = ['open', 'in_progress', 'pending_verification', 'complete'];

const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

export default function TasksPage() {
  const { user, isManagement } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [completing, setCompleting] = useState(null); // task pending a completion note
  const [detailId, setDetailId] = useState(null);      // task id whose detail drawer is open
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => { loadData(); }, [filterStatus, filterArea, filterPriority]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterArea) params.area = filterArea;
      if (filterPriority) params.priority = filterPriority;
      const [taskRes, staffRes] = await Promise.all([
        api.get('/tasks', { params }),
        api.get('/staff'),
      ]);
      setTasks(taskRes.data);
      setStaffList(staffRes.data);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      await api.post('/tasks', {
        ...formData,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : null,
        requires_dual_signoff: formData.requires_dual_signoff === 'true',
      });
      toast.success('Task created');
      setShowForm(false);
      reset();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    }
  };

  // Status transitions that don't need a note (start / submit-for-verification).
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      toast.success('Task updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const verifyTask = async (id) => {
    try {
      await api.patch(`/tasks/${id}/verify`);
      toast.success('Task verified');
      loadData();
    } catch (err) {
      toast.error('Failed to verify');
    }
  };

  const priorityBorder = { high: 'border-l-red-500', medium: 'border-l-amber-500', low: 'border-l-green-500' };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-500 text-sm">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</p>
        </div>
        {isManagement && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-200/50">
            <PlusIcon className="w-5 h-5" /> New Task
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Areas</option>
          {areas.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Priorities</option>
          {priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <ListBulletIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No tasks found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'complete';
            return (
              <div key={task.id} className={`bg-white rounded-2xl border border-l-4 ${priorityBorder[task.priority]} p-4 hover:shadow-md transition-shadow ${isOverdue ? 'bg-red-50/30' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <button onClick={() => setDetailId(task.id)}
                        className={`font-medium text-left hover:text-orange-600 transition-colors ${task.status === 'complete' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {task.title}
                      </button>
                      {task.requires_dual_signoff && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">Dual</span>}
                      {task.auto_source === 'ai_interview_followup' && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium" title="Auto-created from interview AI notes">
                          ✨ From interview
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                      {task.assigned_to_name && <span>Assigned: <StaffLink id={task.assigned_to} name={task.assigned_to_name} /></span>}
                      <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{task.area?.replace(/_/g, ' ')}</span>
                      {task.due_date && (
                        <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
                          Due: {fmtDate(task.due_date)}
                        </span>
                      )}
                      {Number(task.note_count) > 0 && (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" /> {task.note_count}
                        </span>
                      )}
                    </div>
                    {task.status === 'complete' && task.completed_by_name && (
                      <p className="text-xs text-emerald-600 mt-1.5 inline-flex items-center gap-1">
                        <CheckIcon className="w-3.5 h-3.5" />
                        Completed by {task.completed_by_name}{task.completed_at ? ` · ${fmtDate(task.completed_at)}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={task.priority} />
                    <StatusBadge status={task.status} />
                  </div>
                </div>

                <div className="flex gap-2 mt-3 flex-wrap">
                  {task.status === 'open' && (
                    <>
                      <button onClick={() => updateStatus(task.id, 'in_progress')} className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">Start</button>
                      {!task.requires_dual_signoff && (
                        <button onClick={() => setCompleting(task)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                          <CheckIcon className="w-3.5 h-3.5" /> Complete
                        </button>
                      )}
                    </>
                  )}
                  {task.status === 'in_progress' && (
                    task.requires_dual_signoff ? (
                      <button onClick={() => updateStatus(task.id, 'pending_verification')} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <CheckIcon className="w-3.5 h-3.5" /> Submit for Verification
                      </button>
                    ) : (
                      <button onClick={() => setCompleting(task)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <CheckIcon className="w-3.5 h-3.5" /> Complete
                      </button>
                    )
                  )}
                  {task.status === 'pending_verification' && isManagement && (
                    <button onClick={() => verifyTask(task.id)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg">Verify &amp; Complete</button>
                  )}
                  <button onClick={() => setDetailId(task.id)} className="text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                    <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" /> Notes &amp; history
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Completion modal — note required */}
      {completing && (
        <CompleteModal
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={() => { setCompleting(null); loadData(); }}
        />
      )}

      {/* Task detail drawer — notes thread + activity log + reassign */}
      {detailId && (
        <TaskDetailModal
          taskId={detailId}
          staffList={staffList}
          isManagement={isManagement}
          onClose={() => setDetailId(null)}
          onChanged={loadData}
        />
      )}

      {/* New Task Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">New Task</h2>
              <button onClick={() => { setShowForm(false); reset(); }}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input {...register('title', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea {...register('description')} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select {...register('assigned_to')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">Unassigned</option>
                    {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" min={DATE_FLOOR} {...register('due_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select {...register('priority')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                    {priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                  <select {...register('area')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                    {areas.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" {...register('requires_dual_signoff')} value="true" id="dual" className="rounded border-gray-300" />
                <label htmlFor="dual" className="text-sm text-gray-700">Requires dual sign-off</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors">Create Task</button>
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Completion modal (note required) ───────────────────────
function CompleteModal({ task, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!note.trim()) { toast.error('Please add a completion note.'); return; }
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, { status: 'complete', notes: note.trim() });
      toast.success('Task completed');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold">Complete Task</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{task.title}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Completion notes <span className="text-red-500">*</span></label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} autoFocus
              placeholder="What did you find / do? (e.g., Spoke with Ms. Joe — confirmed reliable, eligible for rehire.)"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            <p className="text-[11px] text-gray-500 mt-1">This note is filed permanently on the task and the candidate's record.</p>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={submit} disabled={saving || !note.trim()}
              className="flex-1 bg-emerald-600 text-white font-semibold py-2.5 rounded-xl hover:bg-emerald-700 disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1">
              <CheckIcon className="w-4 h-4" /> {saving ? 'Saving…' : 'Mark Complete'}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Task detail drawer (notes + activity + reassign) ───────
export function TaskDetailModal({ taskId, staffList, isManagement, onClose, onChanged }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [reassignId, setReassignId] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
      setReassignId(data.assigned_to ? String(data.assigned_to) : '');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load task');
      onClose();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [taskId]); // eslint-disable-line

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/notes`, { body: note.trim() });
      setNote('');
      load();
      onChanged();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add note'); }
  };

  const reassign = async () => {
    try {
      await api.patch(`/tasks/${taskId}/reassign`, { assigned_to: reassignId ? parseInt(reassignId) : null });
      toast.success('Task reassigned');
      load();
      onChanged();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to reassign'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold pr-6">{task?.title || 'Task'}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>

        {loading || !task ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Meta */}
            <div className="flex flex-wrap gap-2 text-xs">
              <StatusBadge status={task.status} />
              <StatusBadge status={task.priority} />
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{task.area?.replace(/_/g, ' ')}</span>
              {task.assigned_to_name && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Assigned: {task.assigned_to_name}</span>}
              {task.due_date && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Due {fmtDate(task.due_date)}</span>}
            </div>
            {task.description && <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>}
            {task.status === 'complete' && task.completed_by_name && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-800">
                <span className="font-medium">Completed</span> by {task.completed_by_name} · {fmtDateTime(task.completed_at)}
                {task.verified_by_name && <div className="text-xs mt-0.5">Verified by {task.verified_by_name} · {fmtDateTime(task.verified_at)}</div>}
              </div>
            )}

            {/* Reassign — management only */}
            {isManagement && (
              <div className="border border-gray-200 rounded-xl p-3">
                <label className="block text-xs font-medium text-gray-600 mb-1 inline-flex items-center gap-1">
                  <ArrowsRightLeftIcon className="w-3.5 h-3.5" /> Reassign
                </label>
                <div className="flex gap-2">
                  <select value={reassignId} onChange={(e) => setReassignId(e.target.value)}
                    className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">Unassigned</option>
                    {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </select>
                  <button onClick={reassign} disabled={reassignId === (task.assigned_to ? String(task.assigned_to) : '')}
                    className="bg-orange-500 text-white text-sm px-4 py-2 rounded-xl hover:bg-orange-600 disabled:opacity-40">
                    Reassign
                  </button>
                </div>
              </div>
            )}

            {/* Add note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Add a note</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Progress update, context, follow-up…"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
              <div className="flex justify-end mt-2">
                <button onClick={addNote} disabled={!note.trim()}
                  className="bg-orange-500 text-white text-sm px-4 py-2 rounded-xl hover:bg-orange-600 disabled:opacity-40">Add Note</button>
              </div>
            </div>

            {/* Notes thread */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 inline-flex items-center gap-1">
                <ChatBubbleLeftRightIcon className="w-4 h-4" /> Notes ({task.notes?.length || 0})
              </h3>
              {(task.notes?.length || 0) === 0 ? (
                <p className="text-sm text-gray-400">No notes yet.</p>
              ) : (
                <div className="space-y-2">
                  {task.notes.map(n => (
                    <div key={n.id} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {n.kind === 'completion' && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Completion</span>}
                        <span className="text-xs text-gray-500">
                          {n.author_first ? `${n.author_first} ${n.author_last || ''}` : 'System'} · {fmtDateTime(n.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Activity log */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 inline-flex items-center gap-1">
                <ClockIcon className="w-4 h-4" /> Activity
              </h3>
              {(task.activity?.length || 0) === 0 ? (
                <p className="text-sm text-gray-400">No activity yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {task.activity.map(a => (
                    <li key={a.id} className="text-xs text-gray-500 flex gap-2">
                      <span className="text-gray-300">•</span>
                      <span>
                        <span className="text-gray-700 font-medium">{a.actor_first ? `${a.actor_first} ${a.actor_last || ''}` : 'System'}</span>
                        {' '}{a.detail || a.action} · {fmtDateTime(a.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
